import { type ReactNode } from 'react';

import { css } from '@compiled/react';
import { FormattedMessage, useIntl } from 'react-intl';

import { media } from '@atlaskit/primitives';
import UFOSegment from '@atlaskit/react-ufo/segment';
import { token } from '@atlaskit/tokens';
import { Editions, MeResponse, ProductKeys } from '@atlassiansox/asc-stargate';

import { Adg4Button } from '@/components/adg4/button/src/button';
import { Adg4TextUnderline } from '@/components/adg4/text-underline/src/text-underline';
import { useCrossFlowFrontendTouchpoint } from '@/components/cross-flow/touchpoint/use-touchpoint/index';
import { AccountCreation } from '@/components/signup/account-creation/account-creation';
import { Input } from '@/components/signup/account-creation/components/input/input';
import { Social } from '@/components/signup/account-creation/components/social/social';
import { Submit } from '@/components/signup/account-creation/components/submit/submit';
import TryIntent from '@/components/try-intent/src/try-intent';
import { Picture } from '@/components/wpl/image/src/image';
import { wacToken } from '@/libraries/design/foundations';
import { useLocalizedHref } from '@/libraries/web-framework/src/localization/use-localized-href';
import { TypographyStyles } from '@/libraries/wpl/foundations/src/typography-styles';
import { useFeatureExposed } from '@/utils/feature-exposed/use-feature-exposed';

import HeaderTextBlock from './header-text-block';

const sectionStyle = css({
	paddingBlockEnd: token('space.200'),
	overflow: 'hidden',
});

const relative = css({
	position: 'relative',
	padding: '0 24px',
	'&::before': {
		content: '',
		position: 'absolute',
		insetInlineStart: 0,
		insetBlockStart: 0,
		minWidth: 1440,
		width: '100vw',
		height: '100%',
		background: wacToken('color.extended.blue100'),
		clipPath: 'polygon(0 0, 100% 0, 100% 90%, 0 100%)',
		zIndex: -1,
	},
});

const wrapper = css({
	maxWidth: '1066px',
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	gap: '3rem',
	margin: 'auto',
	[media.below.sm]: {
		flexDirection: 'column',
		gap: 0,
	},
});

const embeddedSignupStyle = css({
	width: '369px',
	padding: '3rem 0',
	[media.below.sm]: {
		paddingBlockStart: 0,
	},
	[media.above.md]: {
		flexShrink: 0,
	},
});
const submitWrapperStyle = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	button: {
		fontSize: '1rem',
		paddingBlock: '6px',
		minHeight: '40px',
	},
});

const socialWrapperStyle = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	ul: {
		gridTemplateColumns: 'repeat(3, 1fr)',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	span: {
		display: 'none',
	},
});

export const HelloTeamworkHeader = ({ lineBreak = false }: { lineBreak?: boolean }) => {
	return (
		<FormattedMessage
			id="ConfluenceSoftwarePage.v4.heroHeader"
			defaultMessage="Goodbye silos, <bold-text>hello</bold-text> <underline-comp>teamwork</underline-comp>"
			values={{
				'bold-text': (node: ReactNode) => (
					<>
						{lineBreak && <br />}
						<span className="superhero">{node}</span>
					</>
				),
				'underline-comp': (node: ReactNode) => (
					<Adg4TextUnderline
						underlineInset="-28%"
						underline={
							<Picture
								image={{
									src: 'https://dam-cdn.atl.orangelogic.com/AssetLink/i01olj0l1c7t5w32dncm1wn5853ibm16.svg',
									alt: 'hero underline',
								}}
							/>
						}
					>
						{node}
					</Adg4TextUnderline>
				),
			}}
		/>
	);
};

type HeroTextSectionProps = {
	userProfile?: MeResponse | null;
};

const HeroTextSection = ({ userProfile }: HeroTextSectionProps) => {
	const intl = useIntl();
	const ctaText = intl.formatMessage({
		id: 'ConfluenceSoftwarePage.v4.heroCTA',
		defaultMessage: 'Get Confluence free',
	});

	const signupLink = useLocalizedHref('/software/confluence/free');
	const { openCrossFlow } = useCrossFlowFrontendTouchpoint({
		productKey: ProductKeys.CONFLUENCE,
		edition: Editions.FREE,
		redirectUrl: signupLink,
	});

	const embeddedSignup = !userProfile;
	useFeatureExposed('cfind-2432_embedded_sign-up_confluence_unpaid_pt', 'Test', {
		conditions: [embeddedSignup],
	});

	return (
		<UFOSegment name="confluence-pt-cfind-1234-heroTextSection">
			<TypographyStyles>
				<section css={sectionStyle}>
					<div css={relative}>
						<div
							css={[
								wrapper,
								embeddedSignup &&
									css({
										justifyContent: 'space-between',
									}),
							]}
						>
							<HeaderTextBlock
								header={<HelloTeamworkHeader lineBreak={embeddedSignup} />}
								subHeader={
									<FormattedMessage
										id="ConfluenceSoftwarePage.v4.heroSubHeading"
										defaultMessage="Create, share, and harness knowledge across teams"
									/>
								}
								leftAligned={embeddedSignup}
								cta={
									!embeddedSignup && (
										<TryIntent
											intentType={'button'}
											containerId={'heroSection'}
											productKey={'confluence'}
											edition={'free'}
										>
											<Adg4Button
												key={'confluence'}
												variant="primary"
												size="large"
												color="blue"
												onClick={openCrossFlow}
												href={signupLink}
												dataEventProps={{
													'data-product-key': 'confluence',
													'data-event': 'clicked',
													'data-event-component': 'linkButton',
													'data-event-container': 'heroSection',
													'data-label': 'hero-get-confluence-free',
													'data-label-english': 'Get Confluence free',
													'data-track-event': 'confluence-pt.get-confluence-free.cta',
												}}
											>
												{ctaText}
											</Adg4Button>
										</TryIntent>
									)
								}
							/>
							{embeddedSignup && (
								<div css={embeddedSignupStyle}>
									<AccountCreation
										Input={<Input helpText={null} />}
										Submit={
											<div css={submitWrapperStyle}>
												<Submit />
											</div>
										}
										Social={
											<div css={socialWrapperStyle}>
												<Social
													providers={{
														google: 'Google',
														apple: 'Apple',
														slack: 'Slack',
													}}
												/>
											</div>
										}
										Login={null}
									/>
								</div>
							)}
						</div>
					</div>
				</section>
			</TypographyStyles>
		</UFOSegment>
	);
};

HeroTextSection.displayName = 'HeroTextSection';

export default HeroTextSection;
